import React, { useState } from "react";
import {
    Badge,
    Button,
    Center,
    Group,
    Loader,
    Modal,
    Paper,
    Stack,
    Text,
} from "@mantine/core";

export type ShiftParticipant = {
    personId: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    status: string;
    createdAt?: string;
};

export type ShiftParticipantsResponse = {
    shiftId: number;
    maxParticipants: number;
    counts: { confirmed: number; waitlist: number; cancelled: number };
    confirmed: ShiftParticipant[];
    waitlist: ShiftParticipant[];
    cancelled: ShiftParticipant[];
};

export default function ParticipantsModal({
    opened,
    onClose,
    title,
    loading,
    data,
    onRefresh,
}: {
    opened: boolean;
    onClose: () => void;
    title: string;
    loading: boolean;
    data: ShiftParticipantsResponse | null;
    onRefresh?: () => Promise<void> | void;
}) {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const [movingPersonId, setMovingPersonId] = useState<number | null>(null);

    const isFull =
        !!data &&
        data.maxParticipants > 0 &&
        data.counts.confirmed >= data.maxParticipants;

    const handleMoveToConfirmed = async (personId: number) => {
        if (!data?.shiftId) return;

        try {
            setMovingPersonId(personId);

            const res = await fetch(`${apiUrl}/api/shifts/${data.shiftId}/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    personId,
                    status: "Tilmeldt",
                }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Kunne ikke flytte person til tilmeldt.");
            }

            if (onRefresh) {
                await onRefresh();
            }
        } catch (err) {
            console.error(err);
            alert("Kunne ikke flytte personen til tilmeldt.");
        } finally {
            setMovingPersonId(null);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} size="lg">
            {loading ? (
                <Center>
                    <Loader />
                </Center>
            ) : !data ? (
                <Text c="dimmed">Ingen data.</Text>
            ) : (
                <Stack gap="md">
                    <Group>
                        <Badge variant="light">Tilmeldt: {data.counts.confirmed}</Badge>
                        <Badge variant="light">Venteliste: {data.counts.waitlist}</Badge>
                        <Badge variant="light">Frameldt: {data.counts.cancelled}</Badge>
                        <Badge color="gray" variant="light">Max: {data.maxParticipants}</Badge>
                    </Group>

                    <Stack gap="xs">
                        <Text fw={700}>Tilmeldte</Text>
                        {data.confirmed.length === 0 ? (
                            <Text c="dimmed" size="sm">Ingen tilmeldte endnu</Text>
                        ) : (
                            data.confirmed.map((p) => (
                                <Paper key={`${p.personId}-c`} withBorder p="sm" radius="md">
                                    <Group justify="space-between">
                                        <div>
                                            <Text fw={600}>{p.firstName} {p.lastName}</Text>
                                            <Text size="sm" c="dimmed">
                                                {p.email || ""}{p.email && p.phone ? " · " : ""}{p.phone || ""}
                                            </Text>
                                        </div>
                                        <Badge color="green" variant="light">Tilmeldt</Badge>
                                    </Group>
                                </Paper>
                            ))
                        )}
                    </Stack>

                    <Stack gap="xs">
                        <Text fw={700}>Venteliste</Text>
                        {data.waitlist.length === 0 ? (
                            <Text c="dimmed" size="sm">Ingen på venteliste</Text>
                        ) : (
                            data.waitlist.map((p) => (
                                <Paper key={`${p.personId}-w`} withBorder p="sm" radius="md">
                                    <Group justify="space-between" align="center">
                                        <div>
                                            <Text fw={600}>{p.firstName} {p.lastName}</Text>
                                            <Text size="sm" c="dimmed">
                                                {p.email || ""}{p.email && p.phone ? " · " : ""}{p.phone || ""}
                                            </Text>
                                        </div>

                                        <Group gap="sm">
                                            <Badge color="yellow" variant="light">Venteliste</Badge>
                                            <Button
                                                size="xs"
                                                disabled={isFull}
                                                onClick={() => handleMoveToConfirmed(p.personId)}
                                                loading={movingPersonId === p.personId}
                                            >
                                                Flyt til tilmeldt
                                            </Button>
                                        </Group>
                                    </Group>
                                </Paper>
                            ))
                        )}
                    </Stack>
                </Stack>
            )}
        </Modal>
    );
}